import { Box, Link, Stack, Typography } from "@mui/material";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive";
import useAiEscapeRoomOpen from "@src/hooks/useAiEscapeRoomOpen";
import {
  LOCAL_STORAGE_KEYS,
  compareTime,
  goldGradientStyle,
  uiContainerTransitionMs,
  uiOpacityHover,
} from "@src/utils";
import React, { FC, useEffect, useState } from "react";

const FeaturePopup: FC = () => {
  const showPlayNow = useAiEscapeRoomOpen();
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const url = typeof window !== "undefined" ? window.location.href : "";

    const currentDate = new Date();
    const startDate = new Date();
    const endDate = new Date();

    // October 1st 2024 00:00:00
    startDate.setTime(1727740800000);

    // Tuesday, April 1, 2025 11:59:59 PM
    // endDate.setTime(743551999000);
    endDate.setTime(1743551999000);

    const popupDismissedValue = localStorage.getItem(
      LOCAL_STORAGE_KEYS.popupRedirectAiChallengeOne,
    );

    if (
      compareTime(currentDate, startDate) &&
      compareTime(endDate, currentDate) &&
      !url.includes("/landing/ai-escape-room")
    ) {
      if (!popupDismissedValue || popupDismissedValue !== "true") {
        setShowPopup(true);
      }
    }
  }, []);

  const handleClose = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    setShowPopup(false);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.popupRedirectAiChallengeOne,
      "true",
    );
  };

  return (
    <Link
      href="/landing/ai-escape-room/"
      target="_blank"
      sx={{
        display: showPopup ? "block" : "none",
        position: "fixed",
        bottom: "24px",
        left: "24px",
        right: {
          xs: "24px",
          sm: "auto",
        },
        width: {
          xs: "calc(100%-48px)",
          sm: "320px",
        },
        zIndex: 10,
        textDecoration: "none",
        boxShadow: "0px 4px 14px -5px rgba(0,0,0,0.1)",
        backgroundColor: Colors.PrimaryContrast,
        borderRadius: "15px",
        transition: "box-shadow 200ms ease-out",
        opacity: 1, // Overriding default link hover state
        ":hover": {
          opacity: 1, // Overriding default link hover state
          textDecoration: "none",
          boxShadow: "0px 4px 14px -5px rgba(0,0,0,0.4)",
        },
      }}
    >
      <Box
        sx={{
          padding: {
            xs: "16px",
            sm: "18px",
          },
        }}
      >
        <Stack
          sx={{
            gap: {
              xs: "10px",
              sm: "4px",
              md: "10px",
            },
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: Colors.TextDark,
                fontSize: {
                  xs: "14px",
                  sm: "16px",
                  md: "20px",
                },
                fontWeight: 800,
                letterSpacing: "-0.5px",
                fontFamily: "Inter",
                lineHeight: "1.5",
              }}
              variant="h3"
            >
              Outsmart our AI.{" "}
              <Box
                component="span"
                sx={{
                  background: goldGradientStyle,
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                Play now
              </Box>{" "}
            </Typography>

            <Box
              role="button"
              onClick={(e) => {
                handleClose(e);
              }}
              sx={{
                width: "16px",
                height: "16px",
                position: "relative",
                backgroundColor: {
                  xs: "transparent",
                  sm: "#17B26A",
                },
                borderRadius: "50%",
                border: {
                  xs: "none",
                  sm: "solid 3px #DCFAE6",
                },
                "&:hover": {
                  border: "none",
                  backgroundColor: "transparent",
                  color: Colors.TextDark,
                  "&:before": {
                    content: '"✕"',
                    top: "-1px",
                    left: "4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                  },
                },
                "&:before": {
                  content: {
                    xs: '"x"',
                    sm: '""',
                  },
                  fontSize: "24px",
                  top: "-14px",
                  left: "-5px",
                  position: "absolute",
                },
              }}
            ></Box>
          </Stack>

          <Stack
            sx={{
              sup: {
                verticalAlign: "top",
                fontSize: "1em",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: "14px",
                },
                fontFamily: "Inter",
                fontWeight: 400,
                color: Colors.TextSecondary,
                lineHeight: "1.5",
                width: {
                  xs: "100%",
                  md: "90%",
                },
              }}
            >
              Play our AI Escape Room Challenge to test your prompt injection
              skills.
            </Typography>

            <Box
              sx={{
                marginTop: "16px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "inline-block",
                  color: Colors.PrimaryContrast,
                  background: Colors.PrimaryGradient,
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "15px",
                  fontFamily: "Inter",
                  padding: "7px 32px",
                  opacity: 1,
                  transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
                  "&:hover": {
                    opacity: uiOpacityHover,
                    cursor: "pointer",
                  },
                }}
              >
                {showPlayNow ? "Play now!" : "Register Now"}
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Link>
  );
};

export default FeaturePopup;
